import { graphql } from 'gatsby'
import React from 'react'
import styled from 'styled-components'
import isDarkColor from '../../helpers/isDarkColor'
import { isContentfulType } from '../../lib/contentful'
import renderDocumentToReactComponents from '../../modules/contentful/renderDocumentToReactComponents/renderDocumentToReactComponents'
import { WhiteContainer } from '../../templates/customPageTemplate'
import theme from '../atoms/theme'
import Container from '../container'
import Card from '../molecules/card'
import InfoCard from '../molecules/infoCard'
import ImageBlock from './ImageBlock'
import ImageGridBlock from './ImageGridBlock'

type ColumnBlockProps = {
  block?: Queries.ContentfulContentSetBlock
  short?: boolean
}

const { colours, breakpoints } = theme

export const StyledTextBlock = styled.div`
  padding: 0;
  margin: 2rem 0 0 0;
  p {
    margin: 0;
  }
`

export const ContentSetContainer = styled(Container)`
  margin-bottom: ${(props): string => (props.short ? '3.6rem' : '6.4rem')};
  background-color: ${(props): string => (props.bgcolor ? props.bgcolor : '')};
  padding: ${(props): string => (props.bgcolor ? '4rem 2.4rem' : '0')};
  h2 {
    color: ${(props): string => (props.darkStyle ? colours.light : '')};
    font-size: 3.6rem;
    line-height: 5.8rem;
  }
  ${StyledTextBlock} {
    color: ${(props): string => (props.darkStyle ? colours.light : '')};
  }
  @media only screen and ${breakpoints.toNormalScreen} {
    // margin and width based on bg color prop is to allow blocks with bg color to be full width on mobile
    margin-left: ${(props): string => (props.bgcolor ? '-1.6rem' : '')};
    margin-right: ${(props): string => (props.bgcolor ? '-1.6rem' : '')};
    width: ${(props): string => (props.bgcolor ? 'auto' : '')};
    margin-bottom: 3.6rem;
    padding: ${(props): string => (props.bgcolor ? '4rem 1.6rem' : '0')};
  }
`

export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.maxCol}, minmax(200px, 1fr));
  grid-template-rows: 1fr;
  grid-row-gap: ${(props): string => (props.darkStyle ? '3rem' : '4rem')};
  grid-column-gap: 25px;
  @media only screen and ${breakpoints.toNormalScreen} {
    grid-template-columns: repeat(1, 1fr);
    column-gap: 0;
  }
`

export const DoubleCol = styled.div`
  grid-column: span 2;
`

export const ColumnBlock = (props: ColumnBlockProps): JSX.Element => {
  const { block, short } = props
  const isDark = isDarkColor(block?.backgroundColor)

  return (
    <ContentSetContainer
      bgcolor={block?.backgroundColor}
      darkStyle={isDark}
      short={short}
    >
      {block?.showTitle && <h2>{block.title}</h2>}
      <GridContainer maxCol={block?.columnsPerRow} darkStyle={isDark}>
        {block?.columnContent?.map((column) => {
          if (
            isContentfulType<Queries.ContentfulContentSetBlockColumn>(
              column,
              'ContentfulContentSetBlockColumn',
            )
          ) {
            return (
              <div key={column.contentful_id}>
                {column.body?.map((item) => {
                  if (
                    isContentfulType<Queries.ContentfulCard>(
                      item,
                      'ContentfulCard',
                    )
                  ) {
                    return <Card card={item} darkStyle={isDark} key={item.id} />
                  }
                  if (
                    isContentfulType<Queries.ContentfulInfoCard>(
                      item,
                      'ContentfulInfoCard',
                    )
                  ) {
                    return (
                      <InfoCard
                        infoCard={item}
                        darkStyle={isDark}
                        key={item.id}
                      />
                    )
                  }
                  if (
                    isContentfulType<Queries.ContentfulImageBlock>(
                      item,
                      'ContentfulImageBlock',
                    )
                  ) {
                    return <ImageBlock block={item} />
                  }
                  if (
                    isContentfulType<Queries.ContentfulImageGridBlock>(
                      item,
                      'ContentfulImageGridBlock',
                    )
                  ) {
                    return (
                      <WhiteContainer>
                        <ImageGridBlock data={item} />
                      </WhiteContainer>
                    )
                  }

                  if (
                    item?.body &&
                    isContentfulType<Queries.ContentfulSmallRichTextBlock>(
                      item,
                      'ContentfulSmallRichTextBlock',
                    )
                  ) {
                    return (
                      <StyledTextBlock key={item.id}>
                        {renderDocumentToReactComponents(
                          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                          item.body as { raw: string; references: any[] },
                        )}
                      </StyledTextBlock>
                    )
                  }
                })}
              </div>
            )
          }
          if (
            isContentfulType<Queries.ContentfulContentSetBlockDoubleColumn>(
              column,
              'ContentfulContentSetBlockDoubleColumn',
            )
          ) {
            return (
              <DoubleCol key={column.contentful_id}>
                {column.body?.map((item) => {
                  if (
                    isContentfulType<Queries.ContentfulCard>(
                      item,
                      'ContentfulCard',
                    )
                  ) {
                    return (
                      <Card
                        card={item}
                        darkStyle={isDark}
                        key={item.id}
                        doubleColCard={true}
                      />
                    )
                  }
                })}
              </DoubleCol>
            )
          }
        })}
      </GridContainer>
    </ContentSetContainer>
  )
}

export const ContentfulContentSetBlockFragment = graphql`
  fragment ContentfulContentSetBlockFragment on ContentfulContentSetBlock {
    id
    __typename
    contentful_id
    title
    showTitle
    columnsPerRow
    backgroundColor
    columnContent: columns {
      ... on ContentfulContentSetBlockColumn {
        id
        contentful_id
        __typename
        title
        body {
          ... on Node {
            ... on ContentfulCard {
              ...ContentfulCardFragment
            }
            ... on ContentfulInfoCard {
              ...ContentfulInfoCardFragment
            }
            ... on ContentfulSmallRichTextBlock {
              ...ContentfulSmallRichTextFragment
            }
            ... on ContentfulImageBlock {
              ...ContentfulImageBlockFragment
            }
            ... on ContentfulImageGridBlock {
              ...ContentfulImageGridBlockFragment
            }
          }
        }
      }
      ... on ContentfulContentSetBlockDoubleColumn {
        id
        contentful_id
        __typename
        title
        body {
          ... on ContentfulCard {
            ...ContentfulCardFragment
          }
        }
      }
    }
  }
`
